import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/router";
import { Context, Hooks, Util } from "@xt/xtbase";
import { getFiats, getCryptos, getMethods, getChannels, getBestPlan, getQuote, postOrderBuy, postOrderSell, getLimit, getTradeLimit } from "@/api/v4/payment";
import { get_balances, get_currencyInfoOne } from "@/api/v4/balance";
import { resolveThirdPartyFiatIconUrl, THIRD_PARTY_FIAT_ICON_MAP } from "@/constants/thirdPartyFiatIconMap";
import { getFiatCurrencyIconSvg } from "@/components/Pages/Convert/fiatCurrencyIcons";
import { useStore } from "@/models";
import styles from "./ThirdPartyBuy.module.less";
import { createCreditTranslator, formatDisclaimerText, normalizeDisplayText } from "./i18n";

const { XtContext } = Context;
const { useTranslation } = Hooks;
const { getUrl } = Util;

const TRADE_TYPE_BUY = 0;
const TRADE_TYPE_SELL = 1;
const FIAT_RECHARGE_ICON_CODES = new Set(["AED", "BRL", "SAR", "VND"]);

function safeNumber(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
}

function svgToDataUrl(svg) {
  if (!svg) return "";
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

function resolveFiatIconForThirdParty(item) {
  const code = String(item?.fiat || item?.value || "").toUpperCase();
  if (FIAT_RECHARGE_ICON_CODES.has(code)) {
    const svg = getFiatCurrencyIconSvg(code);
    if (svg) return svgToDataUrl(svg);
  }
  return resolveThirdPartyFiatIconUrl(code, item, null);
}

function formatStatusText(status, tx) {
  const key = String(status || "").toLowerCase();
  if (key === "pending") return tx("otc.WaitingPayment", "Waiting Payment");
  if (key === "completed") return tx("otc.Completed", "Completed");
  if (key === "failed") return tx("fund.Failed", "Failed");
  if (key === "refunded") return tx("credit.Refunded", "Refunded");
  if (key === "cancelled") return tx("cmpt.cancel", "Cancelled");
  if (key === "expired") return tx("cmpt.expired", "Expired");
  return status || "--";
}

function upperCaseFirstLetter(value) {
  if (!value) return value;
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
}

/** OpenC 聚合渠道：接口里 channel 大小写可能为 openc / Openc */
function isOpenCChannel(channel) {
  return String(channel || "").toLowerCase() === "openc";
}

/** OpenC 展示真实子渠道名称，其它渠道保持首字母大写 */
function getChannelDisplayName(channel, subChannelName) {
  if (isOpenCChannel(channel) && subChannelName) return subChannelName;
  return channel ? upperCaseFirstLetter(channel) : "";
}

/** best-plan 返回 chainId 可能为 null，下单仍需链 ID 时用币种列表兜底 */
function resolveOrderChainId(selectedChannel, selectedCoin) {
  const fromPlan = selectedChannel?.chainId;
  if (fromPlan != null && fromPlan !== "") return fromPlan;
  const ids = selectedCoin?.chainIds;
  if (Array.isArray(ids) && ids.length > 0) return ids[0];
  return undefined;
}

/**
 * quote 请求：与联调样例一致，OpenC 且 plan 未给 chainId 时不传 chainId；
 * 其它渠道仍传 plan.chainId（若有）。
 */
function resolveQuoteChainId(selectedChannel, selectedCoin, isBuy) {
  const fromPlan = selectedChannel?.chainId;
  if (fromPlan != null && fromPlan !== "") return fromPlan;
  if (isBuy && isOpenCChannel(selectedChannel?.channel)) return undefined;
  if (Array.isArray(selectedCoin?.chainIds) && selectedCoin.chainIds.length > 0) return selectedCoin.chainIds[0];
  return undefined;
}

function createLoginHref() {
  if (typeof window === "undefined") return getUrl("/accounts/login");
  return getUrl(`/accounts/login?backurl=${encodeURIComponent(window.location.href)}`);
}

function cn(...items) {
  return items.filter(Boolean).join(" ");
}

// 与 web-index 的 `AppDropdown` 一致：菜单项的 icon 底色按列表 index 轮换（bgList[index % 5]）
const FIAT_BG_LIST = ["#00C19A", "#1781D4", "#F89D4C", "#5985ED", "#B9F78A"];
function getFiatColorByIndex(index) {
  const i = Number.isFinite(index) ? index : 0;
  return FIAT_BG_LIST[i % FIAT_BG_LIST.length];
}

function getFiatSymbol(item) {
  if (!item) return "";
  if (item.symbol) return String(item.symbol);
  const code = item.value || item.text || "";
  return String(code).charAt(0).toUpperCase();
}

function getCoinColor(code) {
  const upper = String(code || "").toUpperCase();
  if (upper === "BTC") return "#F8A34C";
  if (upper === "ETH") return "#7F8CFF";
  if (upper === "USDT") return "#26A69A";
  return "#5C6373";
}

function getCoinLabel(code) {
  const upper = String(code || "").toUpperCase();
  if (upper === "BTC") return "B";
  if (upper === "ETH") return "E";
  return upper.slice(0, 1) || "?";
}

function BackIcon() {
  return (
    <svg width="10" height="16" viewBox="0 0 10 16" fill="none" aria-hidden="true">
      <path d="M8.5 1.5L1.5 8L8.5 14.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** 与 Convert 页 convert-buy__chevron 一致的下拉三角（表单选择框） */
function SelectChevron({ open = false, className = "" }) {
  return (
    <span
      className={className}
      aria-hidden="true"
      style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s ease" }}
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="6" height="5" viewBox="0 0 6 5" fill="none">
        <path d="M6 0V1.5L3 4.68L0 1.5V0H6Z" fill="currentColor" />
      </svg>
    </span>
  );
}

function HistoryIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <rect x="1" y="1" width="14" height="14" rx="2" stroke="currentColor" strokeWidth="1.2" />
      <path d="M5 5.5H11M5 9H11" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}

function CardMethodIcon() {
  return (
    <svg width="24" height="20" viewBox="0 0 24 20" fill="none" aria-hidden="true">
      <path d="M2.4 19.2C1.74 19.2 1.175 18.965 0.705 18.495C0.235 18.025 0 17.46 0 16.8V2.4C0 1.74 0.235 1.175 0.705 0.705C1.175 0.235 1.74 0 2.4 0H21.6C22.26 0 22.825 0.235 23.295 0.705C23.765 1.175 24 1.74 24 2.4V16.8C24 17.46 23.765 18.025 23.295 18.495C22.825 18.965 22.26 19.2 21.6 19.2H2.4ZM2.4 9.6H21.6V4.8H2.4V9.6Z" fill="#E9C349" />
    </svg>
  );
}

function FiatBadge({ item, index = 0 }) {
  const [failed, setFailed] = useState(false);
  const iconUrl = item?.iconUrl || "";
  const showImg = Boolean(iconUrl) && !failed;
  return (
    <span className={styles.iconShellCurrency} style={showImg ? undefined : { background: getFiatColorByIndex(index) }}>
      {showImg ? (
        <img
          src={iconUrl}
          alt=""
          className={styles.currencyIconImg}
          referrerPolicy="no-referrer"
          onError={() => setFailed(true)}
        />
      ) : (
        <span className={styles.currencyIcon}>{getFiatSymbol(item)}</span>
      )}
    </span>
  );
}

function CoinBadge({ code, iconUrl }) {
  const [failed, setFailed] = useState(false);
  const showImg = Boolean(iconUrl) && !failed;
  return (
    <span className={styles.iconShellCurrency}>
      {showImg ? (
        <img
          src={iconUrl}
          alt=""
          className={styles.currencyIconImg}
          onError={() => setFailed(true)}
        />
      ) : (
        <span className={`${styles.currencyIcon} ${styles.currencyIconCoin}`} style={{ background: getCoinColor(code) }}>
          {getCoinLabel(code)}
        </span>
      )}
    </span>
  );
}

function MethodBadge({ method }) {
  if (method?.iconUrl) {
    return (
      <span className={styles.iconShellMethod}>
        <img src={method.iconUrl} alt="" className={styles.methodIcon} />
      </span>
    );
  }
  return (
    <span className={styles.iconShellMethod}>
      <span className={styles.methodFallback}>
        <CardMethodIcon />
      </span>
    </span>
  );
}

export default function ThirdPartyBuy() {
  const router = useRouter();
  const t = useTranslation();
  const [appState] = React.useContext(XtContext);
  const loginStatus = appState?.loginStatus;
  const rootStore = useStore();
  const preloadedIconUrlsRef = useRef(new Set());

  const tx = useMemo(() => createCreditTranslator(t), [t]);

  const [step, setStep] = useState(1);
  const [bootLoading, setBootLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [tradeType, setTradeType] = useState(TRADE_TYPE_BUY);
  const [payNum, setPayNum] = useState("");
  const [fiatOptions, setFiatOptions] = useState([]);
  const [coinOptions, setCoinOptions] = useState([]);
  const [methodOptions, setMethodOptions] = useState([]);
  const [selectedFiat, setSelectedFiat] = useState(null);
  const [selectedCoin, setSelectedCoin] = useState(null);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [channels, setChannels] = useState([]);
  const [channelLoading, setChannelLoading] = useState(false);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteData, setQuoteData] = useState(null);
  const [limits, setLimits] = useState({ minAmount: 0, maxAmount: 0 });
  const [availableBalance, setAvailableBalance] = useState("0");
  const [withdrawPrecision, setWithdrawPrecision] = useState(8);
  const [agreed, setAgreed] = useState(false);
  const [errorText, setErrorText] = useState("");
  const [openDropdown, setOpenDropdown] = useState("");
  const sourceSelectRef = useRef(null);
  const targetSelectRef = useRef(null);
  const methodSelectRef = useRef(null);

  const isBuy = tradeType === TRADE_TYPE_BUY;
  const loginHref = createLoginHref();
  const sourceLabel = isBuy ? tx("credit.YouPay", "You Pay") : tx("credit.YouPay", "You Pay");
  const targetLabel = isBuy ? tx("credit.YouGet", "You Get") : tx("credit.YouGet", "You Get");

  const minAmount = safeNumber(limits.minAmount);
  const maxAmount = safeNumber(limits.maxAmount);
  const amountNum = safeNumber(payNum);
  const availableNum = safeNumber(availableBalance);
  const sellMin = safeNumber(selectedCoin?.minAmount);

  const amountValid = useMemo(() => {
    if (!amountNum || amountNum <= 0) return false;
    if (isBuy) {
      if (minAmount > 0 && amountNum < minAmount) return false;
      if (maxAmount > 0 && amountNum > maxAmount) return false;
      return true;
    }
    if (sellMin > 0 && amountNum < sellMin) return false;
    if (availableNum > 0 && amountNum > availableNum) return false;
    return true;
  }, [amountNum, availableNum, isBuy, maxAmount, minAmount, sellMin]);

  const isBuyPayAmountInvalid = useMemo(() => {
    if (!isBuy) return false;
    const hasPayInput = String(payNum || "").trim() !== "";
    if (!hasPayInput) return false;
    // 限额 0-0（未就绪）视为超限，有输入即展示提示
    if (minAmount <= 0 && maxAmount <= 0) return true;
    if (amountNum <= 0) return false;
    if (minAmount > 0 && amountNum < minAmount) return true;
    if (maxAmount > 0 && amountNum > maxAmount) return true;
    return false;
  }, [amountNum, isBuy, maxAmount, minAmount, payNum]);

  const quoteInSellRange = useMemo(() => {
    if (!quoteData || !quoteData.amount || isBuy) return true;
    const getNum = safeNumber(quoteData.amount);
    if (minAmount > 0 && getNum < minAmount) return false;
    if (maxAmount > 0 && getNum > maxAmount) return false;
    return true;
  }, [quoteData, isBuy, minAmount, maxAmount]);

  const resetFlowAfterBaseChange = useCallback(() => {
    setStep(1);
    setSelectedChannel(null);
    setChannels([]);
    setQuoteData(null);
    setAgreed(false);
    setErrorText("");
  }, []);

  const loadBaseOptions = useCallback(async () => {
    setBootLoading(true);
    setErrorText("");
    try {
      const queryTradeType = Number(router.query.tradeType);
      const nextTradeType = Number.isFinite(queryTradeType) ? queryTradeType : TRADE_TYPE_BUY;
      const [fiatList, coinList] = await Promise.all([
        getFiats({ params: { tradeType: nextTradeType === TRADE_TYPE_SELL ? "sell" : "buy" }, isAutoPopError: false }),
        getCryptos({ params: { tradeType: nextTradeType === TRADE_TYPE_SELL ? "sell" : "buy" }, isAutoPopError: false }),
      ]);

      const fiats = Array.isArray(fiatList)
        ? fiatList.map((item) => ({
            id: item.id,
            value: item.fiat,
            text: item.fiat,
            symbol: item.symbol,
            minAmount: item.minAmount,
            maxAmount: item.maxAmount,
            iconUrl: resolveFiatIconForThirdParty(item),
          }))
        : [];
      const coins = Array.isArray(coinList)
        ? coinList.map((item) => ({
            id: item.id,
            value: item.crypto,
            text: item.crypto,
            minAmount: item.minAmount,
            chainIds: item.chainIds || [],
          }))
        : [];

      setTradeType(nextTradeType);
      setFiatOptions(fiats);
      setCoinOptions(coins);
      setPayNum(typeof router.query.v === "string" ? router.query.v : "");

      const queryFiat = typeof router.query.currency === "string" ? router.query.currency.toUpperCase() : "";
      const queryCoin = typeof router.query.c === "string" ? router.query.c.toUpperCase() : "";
      const nextFiat = fiats.find((item) => item.value === queryFiat) || fiats[0] || null;
      const nextCoin = coins.find((item) => item.value === queryCoin) || coins[0] || null;

      setSelectedFiat(nextFiat);
      setSelectedCoin(nextCoin);
      setSelectedMethod(null);
      setMethodOptions([]);
      setLimits({ minAmount: 0, maxAmount: 0 });
      resetFlowAfterBaseChange();
    } catch (error) {
      console.error("load third-party buy options failed", error);
      setErrorText(tx("credit.LoadDataFailed", "Failed to load data"));
    } finally {
      setBootLoading(false);
    }
  }, [resetFlowAfterBaseChange, router.query.c, router.query.currency, router.query.tradeType, router.query.v, tx]);

  const preloadIconUrls = useCallback((urls) => {
    if (!Array.isArray(urls) || urls.length === 0) return;
    // 上限：避免一次性预加载过多导致带宽峰值
    const max = 80;
    const unique = [];
    for (let i = 0; i < urls.length && unique.length < max; i += 1) {
      const url = urls[i];
      if (!url || typeof url !== "string") continue;
      if (preloadedIconUrlsRef.current.has(url)) continue;
      preloadedIconUrlsRef.current.add(url);
      unique.push(url);
    }
    if (unique.length === 0) return;

    const run = () => {
      unique.forEach((url) => {
        try {
          const img = new Image();
          img.decoding = "async";
          img.loading = "eager";
          img.referrerPolicy = "no-referrer";
          img.src = url;
        } catch (_) {}
      });
    };

    // 尽量不阻塞首屏交互
    if (typeof window !== "undefined" && typeof window.requestIdleCallback === "function") {
      window.requestIdleCallback(run, { timeout: 1200 });
    } else {
      setTimeout(run, 0);
    }
  }, []);

  const loadMethods = useCallback(async () => {
    if (!selectedFiat || !selectedCoin) return;
    try {
      const list = await getMethods({
        params: {
          fiat: selectedFiat.value,
          crypto: selectedCoin.value,
          tradeType: isBuy ? "buy" : "sell",
        },
        isAutoPopError: false,
      });
      const options = Array.isArray(list)
        ? list.map((item) => ({
            id: item.id,
            text: item.method,
            value: item.id,
            iconUrl: item.iconUrl,
            channel: item.channel,
          }))
        : [];
      setMethodOptions(options);
      const defaultName = typeof router.query.payMethodText === "string" ? router.query.payMethodText : "";
      setSelectedMethod((prev) => {
        if (prev && options.find((item) => item.id === prev.id)) return prev;
        if (defaultName) {
          const hit = options.find((item) => item.text === defaultName);
          if (hit) return hit;
        }
        return options[0] || null;
      });
    } catch (error) {
      console.error("load payment methods failed", error);
      setMethodOptions([]);
      setSelectedMethod(null);
    }
  }, [isBuy, router.query.payMethodText, selectedCoin, selectedFiat]);

  const loadLimits = useCallback(async () => {
    if (!selectedFiat || !selectedCoin || !selectedMethod) return;
    try {
      const res = await getLimit({
        params: {
          fiatId: selectedFiat.id,
          cryptoId: selectedCoin.id,
          methodId: selectedMethod.id,
        },
        isAutoPopError: false,
      });
      setLimits({
        minAmount: res?.minAmount || 0,
        maxAmount: res?.maxAmount || 0,
      });
    } catch (error) {
      console.error("load payment limit failed", error);
      setLimits({ minAmount: 0, maxAmount: 0 });
    }
  }, [selectedCoin, selectedFiat, selectedMethod]);

  const loadSellBalance = useCallback(async () => {
    if (!loginStatus || !selectedCoin || isBuy) {
      setAvailableBalance("0");
      return;
    }
    try {
      const res = await get_balances({
        params: { currencies: selectedCoin.value.toUpperCase() },
        isResFull: true,
        isAutoPopError: false,
      });
      const result = res?.result;
      let nextAvailable = "0";
      if (result?.assets && Array.isArray(result.assets)) {
        const asset = result.assets.find((item) => String(item.currency || "").toUpperCase() === selectedCoin.value.toUpperCase());
        nextAvailable = String(asset?.availableAmount ?? asset?.available ?? "0");
      }
      setAvailableBalance(nextAvailable);
    } catch (error) {
      console.error("load sell balance failed", error);
      setAvailableBalance("0");
    }
  }, [isBuy, loginStatus, selectedCoin]);

  const loadWithdrawPrecision = useCallback(async () => {
    if (!selectedCoin || isBuy) {
      setWithdrawPrecision(8);
      return;
    }
    try {
      const info = await get_currencyInfoOne(selectedCoin.value.toLowerCase(), {
        isAutoPopError: false,
      });
      const list = Array.isArray(info) ? info : Array.isArray(info?.chains) ? info.chains : Array.isArray(info?.chainList) ? info.chainList : [];
      const values = list
        .map((item) => Number(item.withdrawPrecision))
        .filter((item) => Number.isFinite(item) && item >= 0);
      if (values.length > 0) {
        setWithdrawPrecision(Math.min(...values));
      } else {
        setWithdrawPrecision(8);
      }
    } catch (_) {
      setWithdrawPrecision(8);
    }
  }, [isBuy, selectedCoin]);

  useEffect(() => {
    if (!router.isReady) return;
    loadBaseOptions();
  }, [loadBaseOptions, router.isReady]);

  // 预加载加密货币 icon：coinOptions 更新后就提前把 logo 拉到缓存里
  useEffect(() => {
    if (!rootStore?.fund || !Array.isArray(coinOptions) || coinOptions.length === 0) return;
    const urls = coinOptions
      .map((item) => {
        const code = String(item?.value || "").toLowerCase();
        if (!code) return "";
        return rootStore.fund.getCurrencyIcon(code);
      })
      .filter(Boolean);
    preloadIconUrls(urls);
  }, [coinOptions, preloadIconUrls, rootStore?.fund]);

  useEffect(() => {
    preloadIconUrls(Object.values(THIRD_PARTY_FIAT_ICON_MAP));
  }, [preloadIconUrls]);

  useEffect(() => {
    loadMethods();
  }, [loadMethods]);

  useEffect(() => {
    loadLimits();
  }, [loadLimits]);

  useEffect(() => {
    loadSellBalance();
  }, [loadSellBalance]);

  useEffect(() => {
    loadWithdrawPrecision();
  }, [loadWithdrawPrecision]);

  useEffect(() => {
    if (!openDropdown) return undefined;
    const refMap = { source: sourceSelectRef, target: targetSelectRef, method: methodSelectRef };
    const activeRef = refMap[openDropdown];
    const handleDocumentClick = (event) => {
      if (activeRef?.current && !activeRef.current.contains(event.target)) {
        setOpenDropdown("");
      }
    };
    document.addEventListener("mousedown", handleDocumentClick);
    return () => document.removeEventListener("mousedown", handleDocumentClick);
  }, [openDropdown]);

  const handleAmountChange = (event) => {
    const raw = String(event.target.value || "")
      .replace(/[^\d.]/g, "")
      .replace(/\.{2,}/g, ".")
      .replace(/^\./g, "");
    const [intPart, decimalPart = ""] = raw.split(".");
    const precision = isBuy ? 2 : withdrawPrecision;
    const formatted = decimalPart === "" ? intPart : `${intPart}.${decimalPart.slice(0, precision)}`;
    setPayNum(formatted);
    resetFlowAfterBaseChange();
  };

  const fetchChannelOptions = useCallback(async () => {
    if (!selectedFiat || !selectedCoin || !selectedMethod || !amountValid) return;
    setChannelLoading(true);
    setErrorText("");
    try {
      if (isBuy && loginStatus === 1) {
        const limitRes = await getTradeLimit({ isAutoPopError: false });
        if (limitRes?.limit) {
          setErrorText(tx("credit.tradeLimit", "Third-party payment is currently unavailable."));
          setChannelLoading(false);
          return;
        }
      }
      const [channelMeta, planList] = await Promise.all([
        getChannels({ params: { tradeType: isBuy ? "buy" : "sell" }, isAutoPopError: false }),
        getBestPlan({
          params: {
            amount: amountNum,
            tradeType: isBuy ? "buy" : "sell",
            methodId: selectedMethod.id,
            method: selectedMethod.text,
            fiatId: selectedFiat.id,
            fiat: selectedFiat.value,
            cryptoId: selectedCoin.id,
            crypto: selectedCoin.value,
            chainIds: Array.isArray(selectedCoin.chainIds) ? selectedCoin.chainIds.join(",") : "",
          },
          isAutoPopError: false,
        }),
      ]);

      const metaList = Array.isArray(channelMeta) ? channelMeta : [];
      const nextChannels = (Array.isArray(planList) ? planList : []).map((item) => {
        const matchedMeta = metaList.find((meta) => String(meta.channel || "").toLowerCase() === String(item.channel || "").toLowerCase());
        return {
          id: item.channelId,
          channelId: item.channelId,
          channel: item.channel,
          name: getChannelDisplayName(item.channel, item.subChannelName),
          iconUrl: item.iconUrl,
          amount: item.amount,
          best: Boolean(item.best),
          chainId: item.chainId,
          disclaimer: matchedMeta?.disclaimer || "",
          subChannelId: item.subChannelId,
          subChannelName: item.subChannelName,
          bestPlanQuoteId: item.quoteId,
        };
      });

      setChannels(nextChannels);
      const nextSelected = nextChannels.find((item) => item.best) || nextChannels[0] || null;
      setSelectedChannel(nextSelected);
      if (nextChannels.length > 0) {
        setStep(2);
      } else {
        setErrorText(tx("credit.Nopaymentchannel", "No payment channel available"));
      }
    } catch (error) {
      console.error("load channels failed", error);
      setErrorText(tx("credit.Nopaymentchannel", "No payment channel available"));
    } finally {
      setChannelLoading(false);
    }
  }, [amountNum, amountValid, isBuy, loginStatus, selectedCoin, selectedFiat, selectedMethod, tx]);

  const fetchQuote = useCallback(async () => {
    if (!selectedChannel || !selectedFiat || !selectedCoin || !selectedMethod) return;
    setQuoteLoading(true);
    setErrorText("");
    try {
      const quoteChainId = resolveQuoteChainId(selectedChannel, selectedCoin, isBuy);
      const quoteParams = {
        fiatId: selectedFiat.id,
        fiat: selectedFiat.value,
        cryptoId: selectedCoin.id,
        crypto: selectedCoin.value,
        methodId: selectedMethod.id,
        method: selectedMethod.text,
        amount: amountNum,
        channelId: selectedChannel.channelId,
        channel: selectedChannel.channel,
        tradeType: isBuy ? "buy" : "sell",
      };
      if (quoteChainId != null && quoteChainId !== "") {
        quoteParams.chainId = quoteChainId;
      }
      const res = await getQuote({
        params: quoteParams,
        isAutoPopError: false,
      });
      setQuoteData(res || null);
      setAgreed(false);
      setStep(3);
    } catch (error) {
      console.error("get quote failed", error);
      setErrorText(tx("credit.GetQuoteFailed", "Failed to get quote"));
    } finally {
      setQuoteLoading(false);
    }
  }, [amountNum, isBuy, selectedChannel, selectedCoin, selectedFiat, selectedMethod, tx]);

  const handleSubmit = async () => {
    if (!loginStatus) {
      window.location.href = loginHref;
      return;
    }
    if (!quoteData || !selectedChannel || !agreed || !quoteInSellRange) return;
    setSubmitting(true);
    setErrorText("");
    try {
      const orderChainId = resolveOrderChainId(selectedChannel, selectedCoin);
      const openC = isOpenCChannel(selectedChannel.channel) || isOpenCChannel(quoteData.channel);
      const quoteIdForOrder = openC
        ? quoteData.quoteId || quoteData.subChannelId || selectedChannel.subChannelId || selectedChannel.bestPlanQuoteId
        : quoteData.quoteId;
      const payload = {
        fiatId: selectedFiat.id,
        fiat: selectedFiat.value,
        cryptoId: selectedCoin.id,
        crypto: selectedCoin.value,
        chainId: orderChainId,
        methodId: selectedMethod.id,
        method: selectedMethod.text,
        channelId: selectedChannel.channelId,
        channel: selectedChannel.channel,
        tradeType: isBuy ? "buy" : "sell",
        quoteId: quoteIdForOrder,
        subChannelId: quoteData.subChannelId ?? selectedChannel.subChannelId,
        subChannelName: quoteData.subChannelName ?? selectedChannel.subChannelName,
        fiatAmount: isBuy ? amountNum : quoteData.amount,
        cryptoAmount: isBuy ? quoteData.amount : amountNum,
        redirectUrl: getUrl("/credit/records"),
        redirectUrlFailed: getUrl("/credit/records"),
        redirectUrlCanceled: getUrl("/credit/records"),
      };
      const request = isBuy ? postOrderBuy : postOrderSell;
      const res = await request(payload, { errorPop: true });
      if (selectedChannel.channelId === 5) {
        const params = new URLSearchParams(res?.params || {}).toString();
        window.location.href = getUrl(`/credit/itez${params ? `?${params}` : ""}`);
        return;
      }
      if (res?.redirectUrl) {
        window.location.href = res.redirectUrl;
        return;
      }
      window.location.href = getUrl("/credit/records");
    } catch (error) {
      console.error("submit third-party order failed", error);
      const code = Number(error?.code || error?.rc);
      if (code === 802) {
        window.location.href = getUrl("/account/personal");
        return;
      }
      setErrorText(error?.info || error?.msg || tx("credit.SubmitFailed", "Failed to submit"));
    } finally {
      setSubmitting(false);
    }
  };

  const loadingLabel = tx("common.loading", "Loading...");

  const renderLimitHint = () => {
    if (isBuy) {
      if (!isBuyPayAmountInvalid) return null;
      const fiatLabel = selectedFiat?.value || "--";
      return (
        <div className={styles.limitHint}>
          {tx("credit.LimitRange", "Limit {min}-{max} {currency}", {
            min: minAmount || 0,
            max: maxAmount || 0,
            currency: fiatLabel,
          })}
        </div>
      );
    }
    return (
      <div className={styles.hint}>
        {tx("credit.SellBalanceHint", "{currency} : {above} {min}", {
          currency: selectedCoin?.value || "--",
          above: tx("credit.Above", "Above"),
          min: sellMin || 0,
        })}
        {loginStatus
          ? ` · ${tx("credit.BalanceWithAmount", "Balance: {amount}", { amount: availableBalance })}`
          : ""}
      </div>
    );
  };

  const handleBack = () => {
    if (typeof window !== "undefined" && window.history.length > 1) {
      router.back();
      return;
    }
    router.push(getUrl("/"));
  };

  const handleGoRecords = () => {
    router.push(getUrl("/credit/records"));
  };

  const sourceOptions = isBuy ? fiatOptions : coinOptions;
  const targetOptions = isBuy ? coinOptions : fiatOptions;
  const selectedSource = isBuy ? selectedFiat : selectedCoin;
  const selectedTarget = isBuy ? selectedCoin : selectedFiat;
  const submitLabel = isBuy
    ? tx("credit.BuyCoin", "Buy {coin}", {
        coin: selectedCoin?.value || tx("credit.BuyCrypto", "Crypto"),
      })
    : tx("credit.SellCoin", "Sell {coin}", {
        coin: selectedCoin?.value || tx("credit.BuyCrypto", "Crypto"),
      });
  const headerTitle =
    step === 2
      ? tx("credit.SelectPaymentMethod", "Select Payment Method")
      : step === 3
        ? tx("credit.PaymentChannel", "Payment Channel")
        : tx("credit.ThirdPartyTitle", "Third Party");
  const handleHeaderBack = () => {
    if (step === 3) {
      setStep(2);
      return;
    }
    if (step === 2) {
      setStep(1);
      return;
    }
    handleBack();
  };

  const renderOptionIcon = (item, type, optionIndex) => {
    if (type === "fiat") return <FiatBadge item={item} index={optionIndex} />;
    if (type === "coin") {
      const code = String(item?.value || "").toLowerCase();
      const iconUrl = code ? rootStore?.fund?.getCurrencyIcon(code) : "";
      return <CoinBadge code={item?.value} iconUrl={iconUrl} />;
    }
    return <MethodBadge method={item} />;
  };

  const renderSelector = ({
    dropdownKey,
    value,
    placeholder,
    options,
    onSelect,
    type = "fiat",
    disabled = false,
    fullWidth = false,
    payRow = false,
    wrapRef,
  }) => {
    const isOpen = openDropdown === dropdownKey && !disabled;
    const wrapClass = [
      styles.selectWrap,
      fullWidth ? styles.selectWrapFull : "",
      payRow ? styles.selectWrapPayRow : !fullWidth ? styles.selectWrapFlex : "",
    ]
      .filter(Boolean)
      .join(" ");
    return (
      <div ref={wrapRef} className={wrapClass}>
        <button
          type="button"
          className={cn(styles.selectTrigger, payRow && styles.selectTriggerPayRow)}
          onClick={() => {
            if (disabled) return;
            setOpenDropdown((current) => (current === dropdownKey ? "" : dropdownKey));
          }}
          disabled={disabled}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
        >
          <span className={cn(styles.selectValue, payRow && styles.selectValuePayRow)}>
            {value
              ? renderOptionIcon(
                  value,
                  type,
                  options.findIndex((x) => String(x?.id ?? x?.value) === String(value?.id ?? value?.value))
                )
              : null}
            <span
              className={cn(styles.selectText, !value && styles.selectTextPlaceholder, payRow && styles.selectTextFull)}
            >
              {value?.text || placeholder}
            </span>
          </span>
          <span className={styles.selectArrow}>
            <SelectChevron open={isOpen} className={styles.selectChevron} />
          </span>
        </button>
        {isOpen ? (
          <ul className={cn(styles.selectDropdown, payRow && styles.selectDropdownPayRow, fullWidth && styles.selectDropdownFull)} role="listbox">
            {options.length === 0 ? (
              <li className={styles.selectEmpty}>{placeholder}</li>
            ) : (
              options.map((item, idx) => {
                const isActive = String(item?.id ?? item?.value) === String(value?.id ?? value?.value);
                return (
                  <li
                    key={`${dropdownKey}-${item?.id ?? item?.value}`}
                    role="option"
                    aria-selected={isActive}
                    className={cn(styles.selectOption, isActive && styles.selectOptionActive)}
                    onClick={() => {
                      onSelect(item);
                      setOpenDropdown("");
                    }}
                  >
                    <span className={styles.selectValue}>
                      {renderOptionIcon(item, type, idx)}
                      <span className={styles.selectText}>{item?.text || item?.value}</span>
                    </span>
                  </li>
                );
              })
            )}
          </ul>
        ) : null}
      </div>
    );
  };

  return (
    <div className={cn(styles.page, step === 3 && styles.pageStep3)}>
      <div className={styles.topbar}>
        <button type="button" className={cn(styles.navBtn, styles.navBtnBack)} onClick={handleHeaderBack} aria-label={tx("common.back", "Back")}>
          <BackIcon />
        </button>
        <button type="button" className={styles.titleBtn} onClick={() => setOpenDropdown("")}>
          <span>{headerTitle}</span>
        </button>
        <button type="button" className={cn(styles.navBtn, styles.navBtnHistory)} onClick={handleGoRecords} aria-label={tx("orders.history", "Records")}>
          <HistoryIcon />
        </button>
      </div>

      <div className={styles.card}>
        {bootLoading ? <div className={cn(styles.loading, styles.loadingHero)}>{loadingLabel}</div> : null}

        {!bootLoading ? (
          <>
            {step === 1 ? (
              <>
                <div className={cn(styles.toggle, styles.toggleTabs)}>
                  <button
                    type="button"
                    className={tradeType === TRADE_TYPE_BUY ? styles.toggleBtnActive : ""}
                    onClick={() => {
                      if (tradeType === TRADE_TYPE_BUY) return;
                      setTradeType(TRADE_TYPE_BUY);
                      setPayNum("");
                      setOpenDropdown("");
                      resetFlowAfterBaseChange();
                    }}
                  >
                    {tx("credit.Buy", "Buy")}
                  </button>
                  {/* <button
                    type="button"
                    className={tradeType === TRADE_TYPE_SELL ? styles.toggleBtnActive : ""}
                    onClick={() => {
                      if (tradeType === TRADE_TYPE_SELL) return;
                      setTradeType(TRADE_TYPE_SELL);
                      setPayNum("");
                      setOpenDropdown("");
                      resetFlowAfterBaseChange();
                    }}
                  >
                    {tx("credit.Sell", "Sell")}
                  </button> */}
                </div>

                <div className={styles.field}>
                  <div className={styles.label}>{sourceLabel}</div>
                  <div className={styles.controlShell}>
                    <div className={styles.inputRow}>
                      <input
                        value={payNum}
                        onChange={handleAmountChange}
                        placeholder="0.00"
                        className={styles.input}
                        inputMode="decimal"
                      />
                      <span className={styles.fieldDivider} aria-hidden="true" />
                      {renderSelector({
                        dropdownKey: "source",
                        value: selectedSource,
                        placeholder: tx("credit.SelectCurrency", "Select currency"),
                        options: sourceOptions,
                        type: isBuy ? "fiat" : "coin",
                        payRow: true,
                        wrapRef: sourceSelectRef,
                        onSelect: (item) => {
                          if (isBuy) setSelectedFiat(item || null);
                          else setSelectedCoin(item || null);
                          setPayNum("");
                          resetFlowAfterBaseChange();
                        },
                      })}
                    </div>
                  </div>
                  {renderLimitHint()}
                </div>

                <div className={styles.field}>
                  <div className={styles.label}>{targetLabel}</div>
                  <div className={styles.controlShell}>
                    <div className={styles.singleRow}>
                      {renderSelector({
                        dropdownKey: "target",
                        value: selectedTarget,
                        placeholder: tx("credit.SelectCurrency", "Select currency"),
                        options: targetOptions,
                        type: isBuy ? "coin" : "fiat",
                        fullWidth: true,
                        wrapRef: targetSelectRef,
                        onSelect: (item) => {
                          if (isBuy) setSelectedCoin(item || null);
                          else setSelectedFiat(item || null);
                          setPayNum("");
                          resetFlowAfterBaseChange();
                        },
                      })}
                    </div>
                  </div>
                </div>

                <div className={styles.field}>
                  <div className={styles.label}>{tx("credit.PaymentMethod", "Payment Method")}</div>
                  <div className={styles.controlShell}>
                    <div className={styles.singleRow}>
                      {renderSelector({
                        dropdownKey: "method",
                        value: selectedMethod,
                        placeholder:
                          methodOptions.length === 0
                            ? tx("credit.Nopaymentmethoda", "No payment method available")
                            : tx("credit.PaymentMethod", "Payment Method"),
                        options: methodOptions,
                        type: "method",
                        disabled: methodOptions.length === 0,
                        fullWidth: true,
                        wrapRef: methodSelectRef,
                        onSelect: (item) => {
                          setSelectedMethod(item || null);
                          resetFlowAfterBaseChange();
                        },
                      })}
                    </div>
                  </div>
                </div>

                {errorText ? <div className={styles.error}>{errorText}</div> : null}
              </>
            ) : null}

            {step === 2 ? (
              <>
                {errorText ? <div className={styles.error}>{errorText}</div> : null}
                <div className={styles.pageSection}>
                  <div className={styles.channelList}>
                    {channelLoading ? <div className={styles.loading}>{loadingLabel}</div> : null}
                    {!channelLoading && channels.length === 0 ? (
                      <div className={styles.empty}>{tx("credit.Nopaymentchannel", "No payment channel available")}</div>
                    ) : null}
                    {channels.map((item) => (
                      <button
                        key={`${item.channelId}-${item.subChannelId || item.channel}`}
                        type="button"
                        className={cn(styles.channelItem, selectedChannel?.channelId === item.channelId && styles.channelItemActive)}
                        onClick={() => setSelectedChannel(item)}
                      >
                        <div className={styles.channelMain}>
                          {item.iconUrl ? <img src={item.iconUrl} alt="" className={styles.channelIcon} /> : <span className={styles.channelDot} />}
                          <div>
                            <div className={styles.channelName}>
                              {item.name}
                              {item.best ? <span className={styles.bestTag}>{tx("credit.BestChoice", "Best Choice")}</span> : null}
                            </div>
                            <div className={styles.channelDesc}>
                              {tx("credit.ApproxAmount", "Approx. {amount} {currency}", {
                                amount: item.amount,
                                currency: isBuy ? selectedCoin?.value : selectedFiat?.value,
                              })}
                            </div>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </>
            ) : null}

            {step === 3 && quoteData ? (
              <>
                <div className={styles.paymentDetails}>
                  <div className={styles.quoteCard}>
                    <div className={styles.quoteCardAccent} aria-hidden="true" />
                    <div className={styles.quoteCardBody}>
                      <div className={styles.quoteRow}>
                        <span className={styles.quoteRowLabel}>{tx("credit.PaymentChannel", "Payment Channel")}</span>
                        <span className={styles.quoteRowValue}>
                          <span className={styles.quoteRowValueText}>
                            {getChannelDisplayName(quoteData.channel, quoteData.subChannelName) || selectedChannel?.name || "--"}
                          </span>
                          {selectedChannel?.iconUrl ? (
                            <img src={selectedChannel.iconUrl} alt="" className={styles.quoteChannelIcon} />
                          ) : null}
                        </span>
                      </div>
                    <div className={styles.quoteRow}>
                      <span className={styles.quoteRowLabel}>{tx("credit.Price", "Price")}</span>
                      <span className={styles.quoteRowValue}>
                        {tx("credit.PriceApprox", "1{crypto} ≈ {price} {fiat}", {
                          crypto: quoteData.crypto || selectedCoin?.value,
                          price: quoteData.price || "--",
                          fiat: quoteData.fiat || selectedFiat?.value,
                        })}
                      </span>
                    </div>
                    <div className={styles.quoteRow}>
                      <span className={styles.quoteRowLabel}>
                        {isBuy ? tx("credit.Fee", "Fee") : tx("credit.Withdrawal", "Withdrawal Fees")}
                      </span>
                      <span className={styles.quoteRowValue}>{isBuy ? quoteData.fee || "-" : quoteData.xtFee || "-"}</span>
                    </div>
                    <div className={styles.quoteRow}>
                      <span className={styles.quoteRowLabel}>{tx("credit.Total", "Total")}</span>
                      <span className={styles.quoteRowValue}>
                        {quoteData.paymentAmount || "--"}{" "}
                        {isBuy ? quoteData.fiat || selectedFiat?.value : quoteData.crypto || selectedCoin?.value}
                      </span>
                    </div>
                    <div className={cn(styles.quoteRow, styles.quoteRowHighlight)}>
                      <span className={styles.quoteRowLabel}>{tx("credit.Youwillget", "You will get")}</span>
                      <span className={styles.quoteRowValue}>
                        {quoteData.amount || "--"}{" "}
                        {isBuy ? quoteData.crypto || selectedCoin?.value : quoteData.fiat || selectedFiat?.value}
                      </span>
                    </div>
                    </div>
                  </div>
                  {!quoteInSellRange ? (
                    <div className={styles.error}>
                      {tx(
                        "credit.OrderAmountRange",
                        "Orders for your selected payment type must be between {min} {currency} and {max} {currency}",
                        {
                          min: minAmount,
                          max: maxAmount,
                          currency: selectedFiat?.value,
                        }
                      )}
                    </div>
                  ) : null}
                  <div className={styles.paymentTip}>
                    {normalizeDisplayText(tx("credit.depends", "The actual price depends on the market price at the time of transaction."))}
                  </div>
                  <div className={styles.disclaimerBlock}>
                    <div className={styles.disclaimerTitle}>{tx("credit.Disclaimer", "Disclaimer")}</div>
                    <div className={styles.disclaimerText}>
                      {formatDisclaimerText(
                        tx(
                          "credit.DisclaimerDesc",
                          "You will now leave XT and be taken to Mercuryo. please prepare the required information for KYC verification and payment account，Services relating to payments are provided by Mercuryo which is a separate platform owned by a third party. Please read and agree to Mercuryo's Terms of Service before using their service.\nFor any questions relating to payments, please contact support@mercuryo.io. XT does not assume any responsibility for any loss or damage caused by the use of this payment service."
                        )
                      )}
                    </div>
                  </div>
                </div>
              </>
            ) : null}

            <div className={cn(styles.footer, step === 3 && styles.footerPayment)}>
              {step === 3 && quoteData ? (
                <label className={styles.checkRow}>
                  <input className={styles.checkInput} type="checkbox" checked={agreed} onChange={() => setAgreed((value) => !value)} />
                  <span>{normalizeDisplayText(tx("credit.readDisclaimer", "I have read and agree to the Disclaimer"))}</span>
                </label>
              ) : null}
              {step === 1 ? (
                loginStatus ? (
                  <button
                    type="button"
                    className={styles.primaryBtn}
                    disabled={!amountValid || !selectedMethod || channelLoading}
                    onClick={fetchChannelOptions}
                  >
                    {channelLoading ? loadingLabel : submitLabel}
                  </button>
                ) : (
                  <a href={loginHref} className={cn(styles.primaryBtn, styles.linkBtn)}>
                    {tx("credit.login", "Login")}
                  </a>
                )
              ) : null}

              {step === 2 ? (
                <button type="button" className={styles.primaryBtn} disabled={!selectedChannel || quoteLoading} onClick={fetchQuote}>
                  {quoteLoading ? loadingLabel : tx("credit.Confirm", "Confirm")}
                </button>
              ) : null}

              {step === 3 ? (
                loginStatus ? (
                  <button
                    type="button"
                    className={styles.primaryBtn}
                    disabled={!quoteData || !agreed || !quoteInSellRange || submitting}
                    onClick={handleSubmit}
                  >
                    {submitting ? loadingLabel : tx("credit.Confirm", "Confirm")}
                  </button>
                ) : (
                  <a href={loginHref} className={cn(styles.primaryBtn, styles.linkBtn)}>
                    {tx("credit.login", "Login")}
                  </a>
                )
              ) : null}
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

export { formatStatusText };
